import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { toast } from 'sonner';
import { 
  Loader2, ArrowLeft, CheckCircle, Building2, Truck, Clock, 
  DollarSign, FileText, Globe, Lightbulb 
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function NewClientSetup() {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    // Section A - Company & Contact
    company_name: '',
    contact_name: '',
    contact_role: '',
    email: '',
    mobile_phone: '',
    service_area: '',
    
    // Section B - Fleet & Operations
    num_portable_units: '',
    num_ada_units: '',
    num_handwash_stations: '',
    primary_markets: [],
    routes_per_day: '',
    num_trucks_drivers: '',
    
    // Section C - Time & Routing
    minutes_deliver_standard: '',
    minutes_deliver_ada: '',
    minutes_service_unit: '',
    minutes_pickup_unit: '',
    service_freq_construction: '',
    service_freq_construction_other: '',
    service_freq_events: '',
    service_freq_events_other: '',
    
    // Section D - Pricing & Billing
    charge_method: [],
    price_standard_weekly: '',
    price_ada_weekly: '',
    separate_delivery_fees: '',
    delivery_fees_description: '',
    extra_service_fees: '',
    extra_service_fees_description: '',
    
    // Section E - Invoicing & Payments
    billing_method: '',
    billing_method_other: '',
    use_stripe: '',
    payment_terms: '',
    payment_terms_other: '',
    
    // Section F - Customer Portal
    portal_features: [],
    portal_exclusions: '',
    
    // Section G - Extras
    top_headaches: '',
    other_tools: '',
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleMultiSelectToggle = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].includes(value)
        ? prev[field].filter(v => v !== value)
        : [...prev[field], value]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${BACKEND_URL}/api/new-client-setup`, formData);
      setSubmitted(true);
      toast.success('Form submitted successfully!');
    } catch (error) {
      console.error('Submission error:', error);
      toast.error('Failed to submit. Please try again or contact us directly.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-lg text-center">
          <CardContent className="pt-8 pb-8">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-3">Thanks!</h2>
            <p className="text-slate-600 mb-6 text-lg">
              We've received your info and will contact you soon to schedule a call.
            </p>
            <div className="space-y-3">
              <Link to="/admin/login">
                <Button className="w-full bg-[#194461] hover:bg-[#091D2E]">
                  Back to Login
                </Button>
              </Link>
              <Link to="/">
                <Button variant="outline" className="w-full">
                  Visit Demo Homepage
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <Link to="/admin/login" className="inline-flex items-center gap-2 text-slate-600 hover:text-[#194461] mb-4">
            <ArrowLeft size={18} />
            Back to Login
          </Link>
          <div className="flex items-center gap-3 justify-center mb-4">
            <img src="/logo-icon.svg" alt="Tinkle Tent" className="h-12 w-12" />
            <div className="text-left flex flex-col">
              <span className="font-bold text-[#194461] text-xl leading-none">Tinkle Tent</span>
              <span className="text-sm text-slate-500 leading-none -mt-0.5">Rentals</span>
            </div>
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-2">New Client Setup</h1>
          <p className="text-slate-600 max-w-xl mx-auto">
            Tell us about your portable sanitation business and we'll configure a system tailored to your operations.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          
          {/* Section A - Company & Contact */}
          <Card>
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Building2 size={20} className="text-[#52B8E1]" />
                Section A – Company & Contact
              </CardTitle>
            </CardHeader>
            <CardContent className="grid gap-4 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <Label htmlFor="company_name">Company Name *</Label>
                <Input
                  id="company_name"
                  name="company_name"
                  value={formData.company_name}
                  onChange={handleChange}
                  required
                  placeholder="Your Company Name"
                  data-testid="input-company-name"
                />
              </div>
              <div>
                <Label htmlFor="contact_name">Your Name *</Label>
                <Input
                  id="contact_name"
                  name="contact_name"
                  value={formData.contact_name}
                  onChange={handleChange}
                  required
                  placeholder="John Smith"
                  data-testid="input-contact-name"
                />
              </div>
              <div>
                <Label htmlFor="contact_role">Your Role</Label>
                <Input
                  id="contact_role"
                  name="contact_role"
                  value={formData.contact_role}
                  onChange={handleChange}
                  placeholder="Owner, Operations Manager, etc."
                  data-testid="input-contact-role"
                />
              </div>
              <div>
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder="john@company.com"
                  data-testid="input-email"
                />
              </div>
              <div>
                <Label htmlFor="mobile_phone">Mobile Phone</Label>
                <Input
                  id="mobile_phone"
                  name="mobile_phone"
                  type="tel"
                  value={formData.mobile_phone}
                  onChange={handleChange}
                  placeholder="(555) 123-4567"
                  data-testid="input-mobile-phone"
                />
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="service_area">Main Service Area *</Label>
                <Input
                  id="service_area"
                  name="service_area"
                  value={formData.service_area}
                  onChange={handleChange}
                  required
                  placeholder="e.g., Knoxville, TN metro"
                  data-testid="input-service-area"
                />
              </div>
            </CardContent>
          </Card>

          {/* Section B - Fleet & Operations */}
          <Card>
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Truck size={20} className="text-[#52B8E1]" />
                Section B – Fleet & Operations
              </CardTitle>
            </CardHeader>
            <CardContent className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label htmlFor="num_portable_units">Approximate Number of Portable Toilet Units</Label>
                <Input
                  id="num_portable_units"
                  name="num_portable_units"
                  value={formData.num_portable_units}
                  onChange={handleChange}
                  placeholder="e.g., 50"
                  data-testid="input-num-portable-units"
                />
              </div>
              <div>
                <Label htmlFor="num_ada_units">Number of ADA Units</Label>
                <Input
                  id="num_ada_units"
                  name="num_ada_units"
                  value={formData.num_ada_units}
                  onChange={handleChange}
                  placeholder="e.g., 10"
                  data-testid="input-num-ada-units"
                />
              </div>
              <div>
                <Label htmlFor="num_handwash_stations">Number of Handwash Stations</Label>
                <Input
                  id="num_handwash_stations"
                  name="num_handwash_stations"
                  value={formData.num_handwash_stations}
                  onChange={handleChange}
                  placeholder="e.g., 15"
                  data-testid="input-num-handwash"
                />
              </div>
              <div>
                <Label htmlFor="num_trucks_drivers">Number of Trucks/Drivers</Label>
                <Input
                  id="num_trucks_drivers"
                  name="num_trucks_drivers"
                  value={formData.num_trucks_drivers}
                  onChange={handleChange}
                  placeholder="e.g., 3"
                  data-testid="input-num-trucks"
                />
              </div>
              <div>
                <Label htmlFor="routes_per_day">Typical Number of Routes Per Day</Label>
                <Input
                  id="routes_per_day"
                  name="routes_per_day"
                  value={formData.routes_per_day}
                  onChange={handleChange}
                  placeholder="e.g., 4"
                  data-testid="input-routes-per-day"
                />
              </div>
              <div className="sm:col-span-2">
                <Label className="mb-3 block">Primary Markets Served</Label>
                <div className="flex flex-wrap gap-4">
                  {['Construction', 'Events', 'Other'].map((market) => (
                    <label key={market} className="flex items-center gap-2 cursor-pointer">
                      <Checkbox
                        checked={formData.primary_markets.includes(market)}
                        onCheckedChange={() => handleMultiSelectToggle('primary_markets', market)}
                        data-testid={`checkbox-market-${market.toLowerCase()}`}
                      />
                      <span className="text-sm">{market}</span>
                    </label>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Section C - Time & Routing Assumptions */}
          <Card>
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Clock size={20} className="text-[#52B8E1]" />
                Section C – Time & Routing Assumptions
              </CardTitle>
              <CardDescription>How long does each type of stop typically take?</CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label htmlFor="minutes_deliver_standard">Minutes to Deliver a Standard Unit</Label>
                <Input
                  id="minutes_deliver_standard"
                  name="minutes_deliver_standard"
                  value={formData.minutes_deliver_standard}
                  onChange={handleChange}
                  placeholder="e.g., 10"
                  data-testid="input-minutes-deliver-standard"
                />
              </div>
              <div>
                <Label htmlFor="minutes_deliver_ada">Minutes to Deliver an ADA Unit</Label>
                <Input
                  id="minutes_deliver_ada"
                  name="minutes_deliver_ada"
                  value={formData.minutes_deliver_ada}
                  onChange={handleChange}
                  placeholder="e.g., 15"
                  data-testid="input-minutes-deliver-ada"
                />
              </div>
              <div>
                <Label htmlFor="minutes_service_unit">Minutes to Service/Pump/Clean a Unit</Label>
                <Input
                  id="minutes_service_unit"
                  name="minutes_service_unit"
                  value={formData.minutes_service_unit}
                  onChange={handleChange}
                  placeholder="e.g., 7"
                  data-testid="input-minutes-service"
                />
              </div>
              <div>
                <Label htmlFor="minutes_pickup_unit">Minutes to Pick Up a Unit</Label>
                <Input
                  id="minutes_pickup_unit"
                  name="minutes_pickup_unit"
                  value={formData.minutes_pickup_unit}
                  onChange={handleChange}
                  placeholder="e.g., 10"
                  data-testid="input-minutes-pickup"
                />
              </div>
              
              <div className="sm:col-span-2">
                <Label>Service Frequency on Construction Sites</Label>
                <Select 
                  value={formData.service_freq_construction} 
                  onValueChange={(v) => handleSelectChange('service_freq_construction', v)}
                >
                  <SelectTrigger data-testid="select-freq-construction" className="mt-1">
                    <SelectValue placeholder="Select frequency" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="twice_weekly">Twice Weekly</SelectItem>
                    <SelectItem value="daily">Daily</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
                {formData.service_freq_construction === 'other' && (
                  <Input
                    name="service_freq_construction_other"
                    value={formData.service_freq_construction_other}
                    onChange={handleChange}
                    placeholder="Please specify..."
                    className="mt-2"
                    data-testid="input-freq-construction-other"
                  />
                )}
              </div>
              
              <div className="sm:col-span-2">
                <Label>Service Frequency for Multi-Day Events</Label>
                <Select 
                  value={formData.service_freq_events} 
                  onValueChange={(v) => handleSelectChange('service_freq_events', v)}
                >
                  <SelectTrigger data-testid="select-freq-events" className="mt-1">
                    <SelectValue placeholder="Select frequency" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="end_of_event">End of Event Only</SelectItem>
                    <SelectItem value="daily">Daily</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
                {formData.service_freq_events === 'other' && (
                  <Input
                    name="service_freq_events_other"
                    value={formData.service_freq_events_other}
                    onChange={handleChange}
                    placeholder="Please specify..."
                    className="mt-2"
                    data-testid="input-freq-events-other"
                  />
                )}
              </div>
            </CardContent>
          </Card>

          {/* Section D - Pricing & Billing */}
          <Card>
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-2 text-lg">
                <DollarSign size={20} className="text-[#52B8E1]" />
                Section D – Pricing & Billing
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="mb-3 block">How Do You Charge?</Label>
                <div className="flex flex-wrap gap-4">
                  {[
                    { value: 'per_rental', label: 'Per Rental Period (Weekly/Monthly)' },
                    { value: 'per_event', label: 'Per Event' },
                    { value: 'both', label: 'Both' }
                  ].map((option) => (
                    <label key={option.value} className="flex items-center gap-2 cursor-pointer">
                      <Checkbox
                        checked={formData.charge_method.includes(option.value)}
                        onCheckedChange={() => handleMultiSelectToggle('charge_method', option.value)}
                        data-testid={`checkbox-charge-${option.value}`}
                      />
                      <span className="text-sm">{option.label}</span>
                    </label>
                  ))}
                </div>
              </div>
              
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <Label htmlFor="price_standard_weekly">Typical Weekly Price for Standard Unit</Label>
                  <Input
                    id="price_standard_weekly"
                    name="price_standard_weekly"
                    value={formData.price_standard_weekly}
                    onChange={handleChange}
                    placeholder="e.g., $150"
                    data-testid="input-price-standard"
                  />
                </div>
                <div>
                  <Label htmlFor="price_ada_weekly">Typical Weekly Price for ADA Unit</Label>
                  <Input
                    id="price_ada_weekly"
                    name="price_ada_weekly"
                    value={formData.price_ada_weekly}
                    onChange={handleChange}
                    placeholder="e.g., $175"
                    data-testid="input-price-ada"
                  />
                </div>
              </div>
              
              <div>
                <Label className="mb-2 block">Do You Charge Separate Delivery and Pickup Fees?</Label>
                <RadioGroup 
                  value={formData.separate_delivery_fees} 
                  onValueChange={(v) => handleSelectChange('separate_delivery_fees', v)}
                  className="flex gap-6"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="yes" id="delivery-yes" data-testid="radio-delivery-yes" />
                    <Label htmlFor="delivery-yes" className="cursor-pointer">Yes</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="no" id="delivery-no" data-testid="radio-delivery-no" />
                    <Label htmlFor="delivery-no" className="cursor-pointer">No</Label>
                  </div>
                </RadioGroup>
                {formData.separate_delivery_fees === 'yes' && (
                  <Input
                    name="delivery_fees_description"
                    value={formData.delivery_fees_description}
                    onChange={handleChange}
                    placeholder="Describe your delivery/pickup fees..."
                    className="mt-2"
                    data-testid="input-delivery-fees-desc"
                  />
                )}
              </div>
              
              <div>
                <Label className="mb-2 block">Do You Offer Extra Service Visits for an Additional Fee?</Label>
                <RadioGroup 
                  value={formData.extra_service_fees} 
                  onValueChange={(v) => handleSelectChange('extra_service_fees', v)}
                  className="flex gap-6"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="yes" id="extra-yes" data-testid="radio-extra-yes" />
                    <Label htmlFor="extra-yes" className="cursor-pointer">Yes</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="no" id="extra-no" data-testid="radio-extra-no" />
                    <Label htmlFor="extra-no" className="cursor-pointer">No</Label>
                  </div>
                </RadioGroup>
                {formData.extra_service_fees === 'yes' && (
                  <Input
                    name="extra_service_fees_description"
                    value={formData.extra_service_fees_description}
                    onChange={handleChange}
                    placeholder="Describe your extra service fees..."
                    className="mt-2"
                    data-testid="input-extra-fees-desc"
                  />
                )}
              </div>
            </CardContent>
          </Card>

          {/* Section E - Invoicing & Payments */}
          <Card>
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-2 text-lg">
                <FileText size={20} className="text-[#52B8E1]" />
                Section E – Invoicing & Payments
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>How Do You Bill Most Customers Today?</Label>
                <Select 
                  value={formData.billing_method} 
                  onValueChange={(v) => handleSelectChange('billing_method', v)}
                >
                  <SelectTrigger data-testid="select-billing-method" className="mt-1">
                    <SelectValue placeholder="Select billing method" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="paper_check">Paper or Emailed Invoices and Check</SelectItem>
                    <SelectItem value="online_payment">Invoices with Online Payment</SelectItem>
                    <SelectItem value="card_on_file">Card on File or Auto-Pay</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
                {formData.billing_method === 'other' && (
                  <Input
                    name="billing_method_other"
                    value={formData.billing_method_other}
                    onChange={handleChange}
                    placeholder="Please specify..."
                    className="mt-2"
                    data-testid="input-billing-other"
                  />
                )}
              </div>
              
              <div>
                <Label>Do You Want to Use Stripe or Similar for Pay Now Links?</Label>
                <Select 
                  value={formData.use_stripe} 
                  onValueChange={(v) => handleSelectChange('use_stripe', v)}
                >
                  <SelectTrigger data-testid="select-stripe" className="mt-1">
                    <SelectValue placeholder="Select option" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="yes_stripe">Yes, Stripe</SelectItem>
                    <SelectItem value="yes_other">Yes, with a Different Processor</SelectItem>
                    <SelectItem value="not_now">Not Right Now</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Standard Payment Terms</Label>
                <Select 
                  value={formData.payment_terms} 
                  onValueChange={(v) => handleSelectChange('payment_terms', v)}
                >
                  <SelectTrigger data-testid="select-payment-terms" className="mt-1">
                    <SelectValue placeholder="Select terms" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="net_15">Net 15</SelectItem>
                    <SelectItem value="net_30">Net 30</SelectItem>
                    <SelectItem value="pay_before">Pay Before Delivery</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
                {formData.payment_terms === 'other' && (
                  <Input
                    name="payment_terms_other"
                    value={formData.payment_terms_other}
                    onChange={handleChange}
                    placeholder="Please specify..."
                    className="mt-2"
                    data-testid="input-terms-other"
                  />
                )}
              </div>
            </CardContent>
          </Card>

          {/* Section F - Customer Portal */}
          <Card>
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Globe size={20} className="text-[#52B8E1]" />
                Section F – Customer Portal Preferences
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="mb-3 block">Portal Features Desired</Label>
                <div className="grid gap-3 sm:grid-cols-2">
                  {[
                    { value: 'request_quotes', label: 'Request Quotes Online' },
                    { value: 'see_rentals', label: 'See Active Rentals and Next Service Date' },
                    { value: 'request_extra', label: 'Request Extra Service or Extensions' },
                    { value: 'see_invoices', label: 'See Invoices and Pay Online' }
                  ].map((feature) => (
                    <label 
                      key={feature.value} 
                      className="flex items-center gap-2 cursor-pointer p-2 rounded border border-slate-200 hover:bg-slate-50"
                    >
                      <Checkbox
                        checked={formData.portal_features.includes(feature.value)}
                        onCheckedChange={() => handleMultiSelectToggle('portal_features', feature.value)}
                        data-testid={`checkbox-portal-${feature.value}`}
                      />
                      <span className="text-sm">{feature.label}</span>
                    </label>
                  ))}
                </div>
              </div>
              
              <div>
                <Label htmlFor="portal_exclusions">Anything You Do NOT Want Customers to See in the Portal?</Label>
                <Textarea
                  id="portal_exclusions"
                  name="portal_exclusions"
                  value={formData.portal_exclusions}
                  onChange={handleChange}
                  placeholder="e.g., Internal pricing, driver schedules, etc."
                  rows={3}
                  className="mt-1"
                  data-testid="textarea-portal-exclusions"
                />
              </div>
            </CardContent>
          </Card>

          {/* Section G - Extras */}
          <Card>
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Lightbulb size={20} className="text-[#52B8E1]" />
                Section G – Extras and Priorities
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="top_headaches">Top 2-3 Headaches You Would Most Like This System to Fix</Label>
                <Textarea
                  id="top_headaches"
                  name="top_headaches"
                  value={formData.top_headaches}
                  onChange={handleChange}
                  placeholder="Tell us about your biggest operational challenges..."
                  rows={4}
                  className="mt-1"
                  data-testid="textarea-headaches"
                />
              </div>
              
              <div>
                <Label htmlFor="other_tools">Other Tools You Use That We Should Know About or Integrate With</Label>
                <Textarea
                  id="other_tools"
                  name="other_tools"
                  value={formData.other_tools}
                  onChange={handleChange}
                  placeholder="e.g., QuickBooks, Google Calendar, specific GPS systems, etc."
                  rows={3}
                  className="mt-1"
                  data-testid="textarea-other-tools"
                />
              </div>
            </CardContent>
          </Card>

          {/* Submit */}
          <div className="flex flex-col sm:flex-row gap-4 justify-end">
            <Link to="/admin/login">
              <Button type="button" variant="outline" className="w-full sm:w-auto">
                Cancel
              </Button>
            </Link>
            <Button 
              type="submit" 
              className="w-full sm:w-auto bg-[#194461] hover:bg-[#091D2E]"
              disabled={loading}
              data-testid="submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Submitting...
                </>
              ) : (
                'Submit Setup Form'
              )}
            </Button>
          </div>
        </form>

        {/* Footer */}
        <p className="text-center text-sm text-slate-500 mt-8">
          Questions? Contact us at{' '}
          <a href="mailto:jeff@sheffkventures.com" className="text-[#194461] hover:underline">
            jeff@sheffkventures.com
          </a>
        </p>
      </div>
    </div>
  );
}
